import React, { useEffect, useState } from "react";
import { useBalanceSheetStore } from "../../../config/stores";
import { Card, Space, Alert, Spin, Form, Typography } from "antd";
import BalanceSheetTable from "./BalanceSheetTable";
import { find, isEmpty } from "lodash";
import AppButton from "../../../components/common/AppButton";
import AppInput from "../../../components/common/AppInput";
import { AiOutlineFileExcel, AiOutlineReload, AiOutlineFilePdf } from "react-icons/ai";
import dayjs from "dayjs";
import generateBalanceSheetPdf from "../../../config/services/balanceSheetPdf";

const { Title } = Typography;

const BalanceSheet = () => {
  const balanceSheetStore = useBalanceSheetStore();
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [form] = Form.useForm();
  const [asOfDate, setAsOfDate] = useState(dayjs());

  const getDefaultContext = () => {
    const asOfDateValue = asOfDate ? dayjs(asOfDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");
    return { asOfDate: asOfDateValue };
  };

  useEffect(() => {
    const defaultContext = getDefaultContext();
    form.setFieldsValue({ asOfDate: dayjs(defaultContext.asOfDate) });
  }, []);

  useEffect(() => {
    const defaultContext = getDefaultContext();
    const existingData = find(balanceSheetStore.balanceSheet, (records) => {
      return records?.asOfDate === defaultContext.asOfDate;
    });

    if (existingData) {
      const data = existingData?.data;
      const balanceSheetData = data?.data || data;
      setBalanceSheet(balanceSheetData);
      setHasLoaded(true);
    }
  }, [balanceSheetStore.balanceSheet, asOfDate]);

  const handleGetBalanceSheet = () => {
    const defaultContext = getDefaultContext();
    setHasLoaded(false);
    balanceSheetStore.getBalanceSheet(defaultContext).finally(() => {
      setHasLoaded(true);
    });
  };

  const reloadBalanceSheet = () => {
    handleGetBalanceSheet();
  };

  const downloadBalanceSheet = () => {
    const defaultContext = getDefaultContext();
    const fileName = `BALANCE_SHEET_AS_OF_${defaultContext.asOfDate}.xlsx`;
    balanceSheetStore.downloadBalanceSheet(defaultContext, fileName);
  };

  const downloadBalanceSheetPdf = () => {
    if (balanceSheet) {
      const defaultContext = getDefaultContext();
      generateBalanceSheetPdf(balanceSheet, defaultContext.asOfDate);
    }
  };

  const isLoading =
    balanceSheetStore.gettingBalanceSheet && !hasLoaded;
  const hasError = !isEmpty(balanceSheetStore.balanceSheetError);
  const hasData =
    balanceSheet?.assets ||
    balanceSheet?.liabilities ||
    balanceSheet?.equity;
  const currentAsOfDate = asOfDate ? dayjs(asOfDate).format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD");

  return (
    <div>
      <Card
        bordered={false}
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>
              BALANCE SHEET
            </Title>
            <div style={{ fontSize: "14px", color: "#666", marginTop: "4px" }}>
              As at: {currentAsOfDate}
            </div>
          </div>
        }
        extra={
          <Space>
            <AppButton
              text="RELOAD"
              loadingText="RELOADING..."
              loading={balanceSheetStore.gettingBalanceSheet}
              iconBefore={<AiOutlineReload />}
              onClick={reloadBalanceSheet}
            />
            <AppButton
              text="DOWNLOAD EXCEL"
              danger
              type="dashed"
              loadingText="DOWNLOADING..."
              iconBefore={<AiOutlineFileExcel />}
              loading={balanceSheetStore.downloadingBalanceSheet}
              onClick={downloadBalanceSheet}
              disabled={!hasData}
            />
            <AppButton
              text="DOWNLOAD PDF"
              type="primary"
              loadingText="GENERATING..."
              iconBefore={<AiOutlineFilePdf />}
              onClick={downloadBalanceSheetPdf}
              disabled={!hasData}
            />
          </Space>
        }
      >
        <Form
          form={form}
          layout="inline"
          onFinish={handleGetBalanceSheet}
          style={{ marginBottom: "16px" }}
        >
          <AppInput
            name="asOfDate"
            label="As Of Date"
            type="date"
            rules={[{ required: true, message: "Please select a date" }]}
            inputAttributes={{
              onChange: (date) => {
                setAsOfDate(date);
              },
            }}
          />
          <Form.Item>
            <AppButton
              text="GET BALANCE SHEET"
              loadingText="Loading..."
              loading={balanceSheetStore.gettingBalanceSheet}
              htmlType="submit"
            />
          </Form.Item>
        </Form>
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
            <div style={{ marginTop: "16px" }}>Loading balance sheet...</div>
          </div>
        ) : hasError ? (
          <Alert
            type="error"
            showIcon
            message="Error Loading Balance Sheet"
            description={
              <div>
                <div style={{ marginBottom: "8px" }}>
                  <strong>Error:</strong>{" "}
                  {balanceSheetStore.balanceSheetError?.response?.data
                    ?.server?.message ||
                    balanceSheetStore.balanceSheetError?.response?.data
                      ?.message ||
                    balanceSheetStore.balanceSheetError?.message ||
                    "Unable to load balance sheet."}
                </div>
                {balanceSheetStore.balanceSheetError?.response?.status ===
                  404 && (
                  <div
                    style={{
                      marginTop: "12px",
                      padding: "12px",
                      backgroundColor: "#fff3cd",
                      borderRadius: "4px",
                      fontSize: "12px",
                    }}
                  >
                    <div style={{ marginBottom: "8px" }}>
                      <strong>404 - Resource Not Found</strong>
                    </div>
                    <div style={{ marginBottom: "4px" }}>
                      The endpoint{" "}
                      <code>/api/v1/finance/balance-sheet</code> was not found.
                    </div>
                    <div style={{ marginTop: "8px" }}>
                      <strong>Backend Setup Required:</strong>
                      <ul
                        style={{
                          marginTop: "4px",
                          marginLeft: "20px",
                          padding: 0,
                        }}
                      >
                        <li>
                          Ensure the <code>balanceSheetRouter</code> is mounted
                          in your main finance router:
                        </li>
                        <li
                          style={{
                            fontFamily: "monospace",
                            marginTop: "4px",
                          }}
                        >
                          <code>
                            router.use('/balance-sheet', balanceSheetRouter);
                          </code>
                        </li>
                        <li style={{ marginTop: "8px" }}>
                          Check that the route file is imported and registered
                        </li>
                        <li>
                          Verify the server is running and the route is
                          accessible
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            }
          />
        ) : !hasData ? (
          <Alert
            type="info"
            showIcon
            message="No Balance Sheet Data"
            description="No balance sheet data available for the selected date."
          />
        ) : (
          <BalanceSheetTable balanceSheetData={balanceSheet} />
        )}
      </Card>
    </div>
  );
};

export default BalanceSheet;



